import { ConnectionSupport } from '@/features/print/connectionSupport'
import { ConnectionType } from '@/features/print/constants'
import { useAppSelector } from '@/hooks/redux'
import SunmiPrinter, { SunmiPrinterInfo } from '@/modules/sunmi-printer'
import { printersSelectors } from '@/redux/reducers/printers/selectors'
import { useEffect, useState } from 'react'

interface SunmiDefaultPrinterOption {
    printerInfo: SunmiPrinterInfo | null
    isLoading: boolean
}

export const useSunmiDefaultPrinterOption = (): SunmiDefaultPrinterOption => {
    const printers = useAppSelector(printersSelectors.printers)
    const [printerInfo, setPrinterInfo] = useState<SunmiPrinterInfo | null | undefined>(undefined)

    const isSunmiConnectionSupported = ConnectionSupport.isSupportedConnectionType(
        ConnectionType.SunmiDefault
    )
    const sunmiPrinterAddedState = printers?.some(
        (printer) => printer.connectionOptions.type === ConnectionType.SunmiDefault
    )
    const isSunmiPrinterDefinitelyAdded = sunmiPrinterAddedState === true
    const isSunmiPrinterDefinitelyNotAdded = sunmiPrinterAddedState === false
    const shouldLoadPrinterInfo = isSunmiPrinterDefinitelyNotAdded && isSunmiConnectionSupported

    useEffect(() => {
        if (!shouldLoadPrinterInfo) return

        let isEffectInvocationStillActive = true

        void SunmiPrinter.getDefaultPrinterInfo()
            .catch(() => null)
            .then((info) => {
                if (isEffectInvocationStillActive) setPrinterInfo(info)
            })

        return () => {
            isEffectInvocationStillActive = false
        }
    }, [shouldLoadPrinterInfo])

    if (!isSunmiConnectionSupported || isSunmiPrinterDefinitelyAdded) {
        return { printerInfo: null, isLoading: false }
    }

    return {
        printerInfo: printerInfo ?? null,
        isLoading: printerInfo === undefined,
    }
}
